import {
  DEFAULT_EXCHANGE_RATES,
  accentThemes,
  currencies,
  defaultCategoryBudgets,
  defaultCategoryOrder,
  fontSizes,
  languages
} from "../constants";
import { createId } from "../utils/formatters";

const USERS_KEY = "ledgerflow_users";
const LEGACY_USERS_KEY = "users";
const CURRENT_USER_KEY = "ledgerflow_current_user";
const LEGACY_CURRENT_USER_KEY = "currentUser";

const wait = (ms = 120) => new Promise((resolve) => setTimeout(resolve, ms));

const readJson = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback));
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

const publicUser = (user) => {
  if (!user) return null;
  const { password, confirmPassword, passwordHash, passwordSalt, ...safeUser } = user;
  return safeUser;
};

const storedUser = (user) => {
  if (!user) return null;
  const { password, confirmPassword, ...safeUser } = user;
  return safeUser;
};

const hasAuthMaterial = (user) => Boolean(user?.passwordHash || user?.password);

const normalizeCategoryOrder = (order) => {
  const safeOrder = Array.isArray(order)
    ? order.filter((category) => defaultCategoryOrder.includes(category))
    : [];

  return [
    ...safeOrder,
    ...defaultCategoryOrder.filter((category) => !safeOrder.includes(category))
  ];
};

const normalizeUser = (user) => ({
  ...user,
  id: user.id || createId(),
  fullName: user.fullName || "",
  monthlyBudget: Number(user.monthlyBudget || 1000),
  monthlyIncome: Number(user.monthlyIncome || 0),
  currency: user.currency || currencies[0],
  themeMode: user.themeMode || (user.darkMode ? "dark" : "auto"),
  language: user.language || languages[0],
  fontSize: user.fontSize || fontSizes[1],
  accentColor: user.accentColor || accentThemes[0].value,
  avatarDataUrl: user.avatarDataUrl || "",
  lastLoginAt: user.lastLoginAt || "",
  darkMode: Boolean(user.darkMode),
  exchangeRates: {
    ...DEFAULT_EXCHANGE_RATES,
    ...(user.exchangeRates || {})
  },
  categoryBudgets: {
    ...defaultCategoryBudgets,
    ...(user.categoryBudgets || {})
  },
  categoryOrder: normalizeCategoryOrder(user.categoryOrder)
});

const getUsers = () => {
  const modernUsers = readJson(USERS_KEY, []);
  const legacyUsers = readJson(LEGACY_USERS_KEY, []);

  const usersByEmail = new Map();
  const addUser = (user) => {
    const normalized = normalizeUser(user);
    const key = normalized.email.toLowerCase();
    const existing = usersByEmail.get(key);

    if (existing && !hasAuthMaterial(normalized) && hasAuthMaterial(existing)) {
      usersByEmail.set(key, {
        ...existing,
        ...normalized,
        password: existing.password,
        passwordHash: existing.passwordHash,
        passwordSalt: existing.passwordSalt
      });
      return;
    }

    usersByEmail.set(key, normalized);
  };

  if (Array.isArray(legacyUsers)) {
    legacyUsers.forEach(addUser);
  }

  if (Array.isArray(modernUsers)) {
    modernUsers.forEach(addUser);
  }

  return Array.from(usersByEmail.values());
};

const saveUsers = (users) => {
  writeJson(USERS_KEY, users.map((user) => storedUser(normalizeUser(user))));
};

const hashText = async (value) => {
  if (window.crypto && window.crypto.subtle && window.TextEncoder) {
    const encoded = new TextEncoder().encode(value);
    const digest = await window.crypto.subtle.digest("SHA-256", encoded);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  }

  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return `fallback-${Math.abs(hash)}`;
};

const expenseKey = (userId) => `ledgerflow_expenses_${userId}`;
const goalKey = (userId) => `ledgerflow_goals_${userId}`;
const legacyExpenseKey = (userId) => `expenses_${userId}`;
const legacyGoalKey = (userId) => `goals_${userId}`;

const getUserData = (key, legacyKey) => {
  const modern = readJson(key, null);
  if (Array.isArray(modern)) return modern;

  const legacy = readJson(legacyKey, []);
  if (Array.isArray(legacy) && legacy.length) {
    writeJson(key, legacy);
    return legacy;
  }

  return [];
};

export const storageService = {
  getCurrentUser() {
    const currentUser = readJson(CURRENT_USER_KEY, null) || readJson(LEGACY_CURRENT_USER_KEY, null);
    return publicUser(currentUser ? normalizeUser(currentUser) : null);
  },

  saveCurrentUser(user) {
    const safeUser = publicUser(normalizeUser(user));
    writeJson(CURRENT_USER_KEY, safeUser);
    return safeUser;
  },

  clearCurrentUser() {
    localStorage.removeItem(CURRENT_USER_KEY);
    localStorage.removeItem(LEGACY_CURRENT_USER_KEY);
  },

  async login(email, password) {
    await wait();
    const normalizedEmail = email.trim().toLowerCase();
    const users = getUsers();
    const index = users.findIndex((user) => user.email.toLowerCase() === normalizedEmail);

    if (index < 0) {
      throw new Error("Invalid email or password");
    }

    const user = users[index];
    const isLegacyPassword = user.password && user.password === password;
    const expectedHash = user.passwordSalt ? await hashText(`${user.passwordSalt}:${password}`) : "";
    const isValid = isLegacyPassword || (user.passwordHash && user.passwordHash === expectedHash);

    if (!isValid) {
      throw new Error("Invalid email or password");
    }

    if (isLegacyPassword || !user.passwordHash) {
      const passwordSalt = createId();
      users[index] = {
        ...user,
        password: undefined,
        passwordSalt,
        passwordHash: await hashText(`${passwordSalt}:${password}`)
      };
    }

    users[index] = {
      ...users[index],
      lastLoginAt: new Date().toISOString()
    };
    saveUsers(users);

    return this.saveCurrentUser(users[index]);
  },

  async register(data) {
    await wait();
    const normalizedEmail = data.email.trim().toLowerCase();
    const users = getUsers();

    if (users.some((user) => user.email.toLowerCase() === normalizedEmail)) {
      throw new Error("User already exists");
    }

    const passwordSalt = createId();
    const user = normalizeUser({
      id: createId(),
      fullName: data.fullName.trim(),
      email: normalizedEmail,
      passwordSalt,
      passwordHash: await hashText(`${passwordSalt}:${data.password}`),
      createdAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString()
    });

    users.push(user);
    saveUsers(users);
    return this.saveCurrentUser(user);
  },

  async updateUserSettings(userId, settings) {
    await wait();
    const users = getUsers();
    const index = users.findIndex((user) => user.id === userId);
    const currentUser = normalizeUser({ ...(users[index] || this.getCurrentUser()), ...settings, id: userId });

    if (index >= 0) {
      users[index] = { ...users[index], ...currentUser };
    } else {
      users.push(currentUser);
    }

    saveUsers(users);
    return this.saveCurrentUser(currentUser);
  },

  async getExpenses(userId) {
    await wait(80);
    return getUserData(expenseKey(userId), legacyExpenseKey(userId));
  },

  async saveExpenses(userId, expenses) {
    await wait(80);
    writeJson(expenseKey(userId), expenses);
    return expenses;
  },

  async getGoals(userId) {
    await wait(80);
    return getUserData(goalKey(userId), legacyGoalKey(userId));
  },

  async saveGoals(userId, goals) {
    await wait(80);
    writeJson(goalKey(userId), goals);
    return goals;
  },

  createBackup(user, expenses, goals) {
    return {
      app: "LedgerFlow",
      version: 2,
      exportedAt: new Date().toISOString(),
      user: publicUser(user),
      expenses,
      goals
    };
  },

  async restoreBackup(userId, currentUser, backup) {
    await wait();

    if (!backup || !Array.isArray(backup.expenses) || !Array.isArray(backup.goals)) {
      throw new Error("Backup file is not valid");
    }

    const restoredUser = await this.updateUserSettings(userId, {
      fullName: backup.user?.fullName || currentUser.fullName,
      monthlyBudget: Number(backup.user?.monthlyBudget || currentUser.monthlyBudget || 1000),
      monthlyIncome: Number(backup.user?.monthlyIncome || currentUser.monthlyIncome || 0),
      currency: backup.user?.currency || currentUser.currency || currencies[0],
      themeMode: backup.user?.themeMode || currentUser.themeMode || "auto",
      language: backup.user?.language || currentUser.language || languages[0],
      fontSize: backup.user?.fontSize || currentUser.fontSize || fontSizes[1],
      accentColor: backup.user?.accentColor || currentUser.accentColor || accentThemes[0].value,
      avatarDataUrl: backup.user?.avatarDataUrl || currentUser.avatarDataUrl || "",
      exchangeRates: {
        ...DEFAULT_EXCHANGE_RATES,
        ...(backup.user?.exchangeRates || currentUser.exchangeRates || {})
      },
      categoryBudgets: {
        ...defaultCategoryBudgets,
        ...(backup.user?.categoryBudgets || currentUser.categoryBudgets || {})
      },
      categoryOrder: backup.user?.categoryOrder || currentUser.categoryOrder || defaultCategoryOrder
    });

    const expenses = backup.expenses.map((expense) => ({ ...expense, userId }));
    const goals = backup.goals.map((goal) => ({ ...goal, userId }));

    await this.saveExpenses(userId, expenses);
    await this.saveGoals(userId, goals);

    return { user: restoredUser, expenses, goals };
  }
};
